import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import SubmitForm from "@/pages/about/components/SubmitForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const principles = [
  {
    icon: "ri-links-line",
    title: "来源可追溯",
    desc: "每条依据都标注来源类型与获取方式，无法溯源的素材不会被当作事实使用。",
  },
  {
    icon: "ri-scales-3-line",
    title: "结论有边界",
    desc: "只对可验证的部分下判断，其余明确标注为存疑，不用推测填补空白。",
  },
  {
    icon: "ri-refresh-line",
    title: "更新留痕迹",
    desc: "每次修改都会记录在时间线中，读者可以看到判断是如何随新依据变化的。",
  },
  {
    icon: "ri-shield-user-line",
    title: "保护当事人",
    desc: "与核查无关的个人信息一律打码处理，不放大与争议无关的私人内容。",
  },
];

const milestones = [
  { year: "2024", text: "从一份手工整理的时间线文档开始，记录第一起事件的截图比对过程。" },
  { year: "2025", text: "建立依据分级标准，把证据强度拆成可对照的三档，并公开评分口径。" },
  { year: "2026", text: "上线事件分类与检索，支持按关键词与类别查找历史追踪记录。" },
];

const voices = [
  {
    quote: "喜欢他们标注来源的方式，能一眼看出哪条是原始素材、哪条只是转发截图。",
    name: "苏晓",
    role: "长期读者",
  },
  {
    quote: "更正流程很顺畅，提交后确实在时间线里看到了修改记录，这点很加分。",
    name: "何川",
    role: "素材提供者",
  },
  {
    quote: "不站队、不煽动，只把矛盾点列出来，这种写法在同类内容里很少见。",
    name: "郑云舒",
    role: "媒体观察者",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 蝌蚪爆料的定位与内容原则 | 蝌蚪爆料",
    description:
      "了解蝌蚪爆料的品牌定位、发展理念与内容原则：以可追溯来源、边界清晰的结论和留痕更新，提供网络争议信息的证据链与真假辨析。",
    keywords: "蝌蚪爆料介绍,蝌蚪爆料原则,蝌蚪爆料可信度标准,证据分级,内容原则",
    canonicalPath: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于蝌蚪爆料",
        url: "https://jiajiazizz.cn/about",
        description: "蝌蚪爆料的品牌定位、发展理念与内容原则",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系蝌蚪爆料",
        url: "https://jiajiazizz.cn/about",
        description: "线索投稿与信息更正的反馈渠道",
      },
    ],
  });

  return (
    <SiteLayout>
      <header className="border-b border-background-300 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-2 font-label text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            关于蝌蚪爆料
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            一个把争议信息还原成时间线与证据清单的内容站。我们相信，多数分歧的根源不是没有信息，而是信息被拆散了。
          </p>
        </div>
      </header>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="品牌定位" title="让每一条判断都找得到出处" />
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                蝌蚪爆料不做情绪化的转发聚合，也不替任何一方定性。我们把同一事件在不同渠道出现的版本并列摆出，
                标注每个版本出现的时间、来源与可核对的部分，再给出当前依据能支持到什么程度。
              </p>
              <p>
                站内的事件状态分为四类：<strong className="font-semibold text-foreground-900">已核实</strong>、
                <strong className="font-semibold text-foreground-900">追证中</strong>、
                <strong className="font-semibold text-foreground-900">存疑</strong>、
                <strong className="font-semibold text-foreground-900">已澄清</strong>。
                当新素材出现时，状态会随之调整，改动会写入对应的时间线。
              </p>
              <p>
                我们面向的读者，是那些不想只依赖评论区风向、希望自己看到原始依据再做判断的人。
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-lg border border-background-300">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20minimal%20editorial%20desk%20with%20paper%20timeline%20strips%20and%20annotated%20documents%20arranged%20carefully%2C%20soft%20daylight%2C%20muted%20vermillion%20and%20olive%20accents%2C%20grain%20texture%2C%20calm%20investigative%20atmosphere%2C%20high%20detail&width=800&height=600&seq=about-studio-01&orientation=landscape"
                alt="整齐摆放的时间线条与标注文件的工作台场景"
                className="h-72 w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-300 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="内容原则"
            title="四条不会妥协的底线"
            description="这些原则决定了哪些内容会被收录，以及以什么方式呈现。"
            align="center"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-lg border border-background-300 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="发展理念" title="一步步把方法固化下来" />
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              从手工文档到公开的分级标准，我们更在意方法是否经得起重复验证。
            </p>
          </div>
          <div className="lg:col-span-7">
            <ol className="relative border-l border-background-400 pl-6">
              {milestones.map((m) => (
                <li key={m.year} className="relative pb-8 last:pb-0">
                  <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full border border-background-400 bg-background-50">
                    <span className="h-2.5 w-2.5 rounded-full bg-accent-500" />
                  </span>
                  <span className="font-heading text-lg font-bold text-accent-700">{m.year}</span>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{m.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-y border-background-300 bg-foreground-950">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-wide text-accent-300">
              <span className="h-px w-6 bg-accent-400" />
              用户口碑
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              读者怎么说
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="rounded-lg border border-background-50/15 bg-background-50/5 p-6">
                <i className="ri-double-quotes-l text-2xl text-accent-300" />
                <blockquote className="mt-3 text-sm leading-relaxed text-background-200">{v.quote}</blockquote>
                <figcaption className="mt-4 border-t border-background-50/15 pt-3">
                  <p className="font-heading text-sm font-semibold text-background-50">{v.name}</p>
                  <p className="font-label text-xs text-background-400">{v.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="联系我们"
              title="投稿线索与更正说明"
              description="如果你掌握新的原始素材，或发现已发布内容存在偏差，可以通过下面的表单提交，我们会逐条核对。"
            />
            <div className="mt-6 space-y-3">
              <p className="flex items-center gap-3 text-sm text-foreground-700">
                <i className="ri-mail-line text-primary-600" /> contact@kodo-expose.example.com
              </p>
              <p className="flex items-center gap-3 text-sm text-foreground-700">
                <i className="ri-time-line text-primary-600" /> 核对反馈一般在 3 个工作日内回复
              </p>
              <p className="flex items-start gap-3 text-sm leading-relaxed text-foreground-600">
                <i className="ri-information-line mt-0.5 text-primary-600" />
                为避免误判，请尽量附上原始素材或可访问的原始链接，而不是二次转发内容。
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <SubmitForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}